import {
  getCustomizationsInfo,
  getDefaultCustomizations,
} from '@sr/templates/src';
import { Customization, Resume } from '@sr/types';
import React from 'react';
import { Box, Button, Text } from '../../ui';
import API from '../../utils/api';
import ColorPicker from './ColorPicker';
import PhotoEffectPicker from './PhotoEffectPicker';
import SelectPicker from './SelectPicker';
import { Sentry } from 'utils/sentry';

const TemplateCustomizations = ({ resume }: { resume: Resume }) => {
  const { activeTemplateId, customizations } = resume.style;
  const activeCustomizations = customizations[activeTemplateId];
  const customizationsInfo = getCustomizationsInfo(activeTemplateId);
  const pickers = [];

  if (!activeCustomizations) {
    throw new Error(
      `The resume ${resume.id} is missing customization values for the chosen template ${activeTemplateId}`
    );
  }

  function updateConfig(option: string, value: string | boolean) {
    API.resume.setProperty({
      path: `${resume.id}/style/customizations/${activeTemplateId}`,
      value: {
        ...activeCustomizations,
        [option]: value,
      },
    });
    API.events.logEvent({
      userId: resume.owner,
      eventName: 'Resume Changed Template Option',
      properties: {
        Option: option,
        Value: value,
        'Resume Id': resume.id,
        'Template Id': activeTemplateId,
      },
    });
  }

  function resetColorsToDefault() {
    const defaults = getDefaultCustomizations(activeTemplateId);
    API.resume.setProperty({
      path: `${resume.id}/style/customizations/${activeTemplateId}`,
      value: defaults,
    });
  }

  for (const [key, optionInfo] of Object.entries(customizationsInfo)) {
    const value = activeCustomizations[key as keyof Customization];
    const optionType = optionInfo.type;

    if (typeof value !== 'string') {
      continue;
      Sentry.captureException(
        new Error('Customization option value must be a string'),
        {
          extra: {
            valueType: typeof value,
          },
        }
      );
    }

    if (optionInfo.type === 'color') {
      pickers.push(
        <ColorPicker
          label={optionInfo.label}
          selectedColor={value}
          presets={optionInfo.presets}
          onSelect={(selectedValue) => updateConfig(key, selectedValue)}
          key={key}
        />
      );
    } else if (optionInfo.type === 'select' && typeof value === 'string') {
      pickers.push(
        <SelectPicker
          label={optionInfo.label}
          selected={value}
          values={optionInfo.presets}
          onSelect={(selectedValue) => updateConfig(key, selectedValue)}
          key={key}
        />
      );
    } else {
      Sentry.captureException(new Error('Invalid customization options type'), {
        extra: {
          valueType: optionInfo.type,
        },
      });
    }
  }

  return (
    <Box pt={3} id="gi-preview-color-pickers">
      <Text variant="listHeadingDark" py={1}>
        Color Options
      </Text>
      <Box sx={{ position: 'relative' }}>{pickers}</Box>
      <Button
        variant="link"
        onClick={resetColorsToDefault}
        sx={{
          color: 'blueDark',
          ml: -4,
        }}
      >
        Reset Colors
      </Button>
    </Box>
  );
};

export default TemplateCustomizations;
